import { FC } from 'react';
import { CardLink, PreviewCard } from '../common';
import { CardProps } from '../../sidebar';
import { useTranslation } from 'react-i18next';
import { useCustomPageQuery } from '../../../../../common/queries/use-custom-page-query';
import { Spinner } from '../../../../ui';
import styles from './styles.module.css';
import { useTabNavigation } from '../../hooks/useTabNavigation';
import { ChannelTab } from '../../../../../models/channels/types';

interface CustomPagePreviewProps extends CardProps {
  customPageId: NonNullable<ChannelTab['custom_page_id']>;
  customPageTitle: ChannelTab['title'];
  tabId?: ChannelTab['id'];
}

const CustomPagePreview: FC<CustomPagePreviewProps> = ({
  topicId,
  customPageId,
  customPageTitle,
  tabId,
}: CustomPagePreviewProps) => {
  const { t } = useTranslation();

  const {
    data: customPageData,
    isLoading,
    isError,
  } = useCustomPageQuery(topicId, customPageId);

  const { createSlug } = useTabNavigation({
    topicId,
    tabs: [],
  });

  const errorMessage = t('screens.topics.about.error');

  // Create the correct URL format for custom pages
  const getCustomPageUrl = () => {
    if (tabId) {
      const slug = createSlug(customPageTitle);
      return `/channels/${topicId}/${tabId}-${slug}`;
    }
    // Fallback to old format if no tabId
    return `/channels/${topicId}/custom-${customPageId}`;
  };

  // Extract preview text from custom page content (simple text extraction)
  const getPreviewText = (content: string) => {
    if (!content) return '';

    // Remove HTML tags and get first 150 characters
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    return textContent.length > 150
      ? textContent.substring(0, 150) + '...'
      : textContent;
  };

  return (
    <PreviewCard
      title={customPageTitle}
      link={
        <CardLink
          label={t(
            'screens.topic_page.sidebar.custom_page_preview.view_all',
            'View All'
          )}
          to={getCustomPageUrl()}
        />
      }
    >
      {isError && !isLoading && <div>{errorMessage}</div>}
      {isLoading && <Spinner />}
      {customPageData?.preview_content && (
        <span className={styles.customPagePreviewContent}>
          {getPreviewText(customPageData.preview_content)}
        </span>
      )}
    </PreviewCard>
  );
};

export default CustomPagePreview;
